import { useCallback, useEffect, useRef, useState } from 'react'

import { playGreetingAudio, type PlayAudioRequest } from '@/hooks/api/useGreetings'
import { toast } from '@/lib/toast'

interface UseAudioPreviewOptions {
  /** Translated toast message shown when fetching or playing a preview fails. */
  errorMessage: string
}

interface UseAudioPreviewResult {
  /**
   * Id whose preview is active. Set as soon as a preview is requested (i.e.
   * also while it is still loading), cleared when playback stops, fails, or
   * finishes.
   */
  playingId: number | null
  /** Id whose preview is still being fetched / starting; null once audible. */
  loadingId: number | null
  /**
   * Start the preview for `id`, or stop it when it is already the active one.
   * Starting a preview always stops the current one first.
   */
  toggle: (id: number, params: PlayAudioRequest) => Promise<void>
  /** Stop the active preview (if any) and discard any in-flight fetch. */
  stop: () => void
}

// Stop handle of whichever hook instance currently owns a preview. Starting a
// preview in one component then also stops the one playing in another (e.g.
// the selected-voice card next to the picker list), so previews never overlap.
let stopActivePreview: (() => void) | null = null

/**
 * Shared play/stop state machine for TTS voice previews, used by the admin
 * list, the admin form drawer and the customer voice picker.
 *
 * Audio is fetched via `playGreetingAudio` with `autoplay: false` so the
 * stale-request token can be checked *before* any sound starts. Every path
 * that abandons an audio element (stale fetch, rejected play, natural end,
 * explicit stop) revokes its object URL so the underlying blob can be freed.
 */
export function useAudioPreview({ errorMessage }: UseAudioPreviewOptions): UseAudioPreviewResult {
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Monotonically increasing token identifying the latest preview request.
  // Bumped on every stop, so an in-flight fetch whose token no longer matches
  // knows it went stale (another preview started, or playback was stopped).
  const requestRef = useRef(0)

  const stop = useCallback(() => {
    requestRef.current += 1
    if (audioRef.current) {
      audioRef.current.pause()
      // playGreetingAudio backs the element with an object URL; revoke it so
      // the underlying blob can be freed.
      URL.revokeObjectURL(audioRef.current.src)
      audioRef.current.removeAttribute('src')
      audioRef.current = null
    }
    setPlayingId(null)
    // Also clear the loading state: the token checks in `toggle` deliberately
    // skip their own resets once the token is bumped (stop-during-load), so
    // without this the flag would stick until the next preview.
    setLoadingId(null)
  }, [])

  // Keep the module-level registration in sync: this instance only holds the
  // slot while it actually has an active preview.
  const releaseActive = useCallback(() => {
    if (stopActivePreview === stop) stopActivePreview = null
  }, [stop])

  // Stop playback when the consuming component unmounts. `stop` is stable, so
  // this cleanup effectively runs on unmount only. Consumers whose UI can be
  // dismissed without unmounting (e.g. side panels that keep their children
  // mounted) additionally tie `stop` to their own open/close lifecycle.
  useEffect(
    () => () => {
      stop()
      releaseActive()
    },
    [stop, releaseActive]
  )

  const toggle = useCallback(
    async (id: number, params: PlayAudioRequest) => {
      if (playingId === id) {
        stop()
        releaseActive()
        return
      }
      stop()
      // Also stop whichever OTHER hook instance is currently previewing, and
      // claim the active slot for this one.
      if (stopActivePreview && stopActivePreview !== stop) stopActivePreview()
      stopActivePreview = stop
      const token = requestRef.current
      setPlayingId(id)
      setLoadingId(id)
      // Tracked outside the try so a rejected play() can still dispose the
      // fetched audio (and its object URL) in the catch below.
      let fetchedAudio: HTMLAudioElement | null = null
      try {
        // Fetch without autoplay so the stale-request check below runs before
        // any sound can start.
        const audio = await playGreetingAudio(params, { autoplay: false })
        fetchedAudio = audio
        if (requestRef.current !== token) {
          // Stale request: another preview started (or playback was stopped)
          // while this one was loading. Discard the late audio unplayed.
          URL.revokeObjectURL(audio.src)
          return
        }
        await audio.play()
        if (requestRef.current !== token) {
          // Stopped while playback was starting.
          audio.pause()
          URL.revokeObjectURL(audio.src)
          return
        }
        setLoadingId(null)
        audioRef.current = audio
        audio.onended = () => {
          URL.revokeObjectURL(audio.src)
          audioRef.current = null
          setPlayingId(null)
        }
      } catch (error) {
        // Dispose the fetched audio if ownership never reached audioRef
        // (e.g. play() rejected) so the object URL does not leak.
        if (fetchedAudio && audioRef.current !== fetchedAudio) {
          fetchedAudio.pause()
          URL.revokeObjectURL(fetchedAudio.src)
        }
        if (requestRef.current !== token) return
        console.error('Failed to play audio preview:', error)
        toast.error(errorMessage)
        setLoadingId(null)
        setPlayingId(null)
      }
    },
    [playingId, stop, releaseActive, errorMessage]
  )

  return { playingId, loadingId, toggle, stop }
}

export default useAudioPreview
